import { useState } from "react";
import { Gift, Share2, Check, Users, IndianRupee } from "lucide-react";

import { api } from "../lib/api";
import { useResource } from "../lib/useApi";
import { money, date } from "../lib/fmt";
import { Sheet, Glass, Skeleton, Err, Empty, Pill } from "../ui";
import { Mark } from "../brand/Mark";

/**
 * ReferSheet — the affiliate side of the programme, from the client's end.
 *
 * The client is the affiliate. They submit a name and a number, the studio
 * picks it up as a lead, and the reward is paid on signing. Everything about
 * how much the studio makes on that project stays on the studio side; this
 * screen shows the client only their own reward and its status.
 *
 * The reward amount and the terms come from the server (/portal/referrals),
 * not from a constant here, so the studio can change the offer without a
 * frontend release.
 */

export function ReferSheet({ ctx, onClose }) {
  const { clientName } = ctx;
  const { data, loading, error, reload } = useResource("/portal/referrals");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

    /* The figure is the server's — `referral_reward` in settings — so the offer
     can be changed without a deploy. This fallback only shows in the split
     second before /portal/referrals answers, and on a database where the V8
     migration has not run. */
  const reward = data?.reward_amount ?? 25000;
  const referrals = data?.referrals || [];
  const earned = referrals.reduce((n, r) => n + (r.status === "paid" ? Number(r.reward_amount || 0) : 0), 0);
  const pending = referrals.reduce((n, r) => n + (r.status === "earned" ? Number(r.reward_amount || 0) : 0), 0);

  async function submit() {
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (!name.trim()) return setErr("Who should we call?");
    if (digits.length < 10) return setErr("A 10-digit mobile number, please.");
    setBusy(true);
    setErr("");
    try {
      await api.post("/portal/referrals", { name: name.trim(), phone: digits, note: note.trim() });
      setName(""); setPhone(""); setNote("");
      setSent(true);
      reload();
      setTimeout(() => setSent(false), 4000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function share() {
    const text = `I'm getting my home done with D'leaps and it's been genuinely good. If you're planning yours, talk to them — https://www.dleaps.com`;
    if (navigator.share) navigator.share({ title: "D'leaps", text }).catch(() => {});
    else navigator.clipboard?.writeText(text).catch(() => {});
  }

  return (
    <Sheet title="Refer & earn" onClose={onClose}>
      <Glass strong style={{ padding: 20, marginBottom: 16, textAlign: "center" }}>
        <Mark size={38} />
        <p className="hero-amt" style={{ margin: "10px 0 4px" }}>{money(reward)}</p>
        <p className="muted" style={{ margin: 0, fontSize: ".86rem" }}>
          For every friend who signs a project with us. Paid once their BOQ is approved — no cap on how many.
        </p>
        <button className="btn block" style={{ marginTop: 14 }} onClick={share}>
          <Share2 size={16} /> Share D&rsquo;leaps
        </button>
      </Glass>

      {referrals.length > 0 && (
        <div className="grid2" style={{ marginBottom: 16 }}>
          <div className="neu-in stat"><small>Earned</small><b>{money(earned)}</b></div>
          <div className="neu-in stat"><small>In progress</small><b>{money(pending)}</b></div>
        </div>
      )}

      <p className="sec-title" style={{ padding: "0 0 8px" }}>Introduce someone</p>

      <Err onDismiss={() => setErr("")}>{err}</Err>
      {sent && (
        <Glass tint style={{ padding: 12, marginBottom: 12, display: "flex", gap: 9, alignItems: "center" }}>
          <Check size={16} color="var(--ok)" />
          <span style={{ fontSize: ".86rem" }}>Sent. We will call them within a working day.</span>
        </Glass>
      )}

      <div className="field">
        <label htmlFor="rf-name">Their name</label>
        <input id="rf-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="rf-phone">Their mobile number</label>
        <input id="rf-phone" inputMode="numeric" maxLength={14} value={phone} placeholder="10-digit mobile" onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="rf-note">Anything we should know</label>
        <textarea id="rf-note" rows={3} maxLength={500} value={note} placeholder="3BHK in Kokapet, possession in March." onChange={(e) => setNote(e.target.value)} />
      </div>

      <p className="muted" style={{ fontSize: ".76rem", margin: "0 0 14px" }}>
        Please ask them first. We will say {clientName || "you"} passed on their number, and we will only call once.
      </p>

      <button className="btn primary block" onClick={submit} disabled={busy}>
        {busy ? "Sending…" : "Send their details"}
      </button>

      <p className="sec-title" style={{ padding: "22px 0 8px" }}>Your referrals</p>
      {loading && <Skeleton h={64} />}
      <Err onDismiss={reload}>{error}</Err>
      {!loading && !referrals.length && (
        <Empty icon={Users} title="None yet" note="They appear here with their status the moment you send one." />
      )}
      {referrals.map((r) => (
        <Glass className="row" key={r.id}>
          <span className="row-ic"><Gift size={16} /></span>
          <span className="row-txt">
            <b>{r.name}</b>
            <small>{date(r.created_at)}</small>
          </span>
          <span className="row-end">
            {Number(r.reward_amount) > 0 && <b>{money(r.reward_amount)}</b>}
            <Pill tone={r.status === "paid" ? "ok" : r.status === "lost" ? undefined : "warn"}>
              {LABEL[r.status] || r.status}
            </Pill>
          </span>
        </Glass>
      ))}
    </Sheet>
  );
}

const LABEL = {
  new: "Sent",
  contacted: "We called",
  quoted: "Quoted",
  signed: "Signed",
  earned: "Reward due",
  paid: "Paid",
  lost: "Not this time",
};

export { IndianRupee };
